import { useState, useEffect, useRef } from "react";
import { useAuth } from "../hooks/useAuth";
import { profileService, User } from "../services/profile.service";
import { toast } from "sonner";
import { 
  Camera, 
  Save, 
  X,
  LogOut,
  User as UserIcon,
  Mail,
  Shield,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

// Constants
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'] as const;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIN_NAME_LENGTH = 3;
const MAX_DESCRIPTION_LENGTH = 500;

const ROLE_CONFIG = {
  admin: {
    label: 'Administrador',
    description: 'Administrador com acesso completo ao sistema de gestão de eventos',
    colorClass: 'bg-brand-danger/10 text-status-danger border-brand-danger/30'
  },
  agency: {
    label: 'Agência',
    description: 'Usuário de agência com acesso às soluções e eventos da plataforma',
    colorClass: 'bg-brand-attention/10 text-status-attention border-brand-attention/30'
  },
  promoter: {
    label: 'Promotor',
    description: 'Promotor de eventos com acesso às ferramentas de gestão',
    colorClass: 'bg-primary/10 text-primary border-primary/30'
  },
  user: {
    label: 'Usuário',
    description: 'Usuário da plataforma doo.events',
    colorClass: 'bg-muted text-muted-foreground border-border'
  }
} as const;

export default function Profile() {
  const { logout, refreshUser } = useAuth();
  const [profileData, setProfileData] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });

  // Initialize profile data
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const userData = await profileService.getProfile();
      setProfileData(userData);
      setFormData({
        name: userData.name,
        description: userData.description || ""
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error("Não foi possível carregar os dados do perfil");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    // Validation
    if (formData.name.length < MIN_NAME_LENGTH) {
      toast.error(`O nome deve ter pelo menos ${MIN_NAME_LENGTH} caracteres`);
      return;
    }

    if (formData.description && formData.description.length > MAX_DESCRIPTION_LENGTH) {
      toast.error(`A descrição deve ter no máximo ${MAX_DESCRIPTION_LENGTH} caracteres`);
      return;
    }

    setIsSaving(true);
    try {
      const updatedUser = await profileService.updateProfile({
        name: formData.name,
        description: formData.description || undefined
      });
      
      setProfileData(updatedUser);
      if (refreshUser) {
        await refreshUser();
      }
      setIsEditing(false);
      
      toast.success("Perfil atualizado com sucesso");
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error("Não foi possível salvar as alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (profileData) {
      setFormData({
        name: profileData.name,
        description: profileData.description || ""
      });
    }
    setIsEditing(false);
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!VALID_IMAGE_TYPES.includes(file.type as typeof VALID_IMAGE_TYPES[number])) {
      toast.error("Por favor, selecione uma imagem JPG, PNG ou GIF");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error("A imagem deve ter no máximo 5MB");
      return;
    }

    setIsUploadingAvatar(true);
    try {
      await profileService.uploadAvatar(file);
      
      // Reload profile to get updated avatar
      await loadProfile();
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success("Avatar atualizado com sucesso");
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error("Não foi possível enviar a imagem");
    } finally {
      setIsUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogout = async () => {
    try {
      await profileService.logout();
      await logout();
      // Redirect to Doo Events
      const DOO_EVENTS_URL = import.meta.env.VITE_DOO_EVENTS_URL || 'http://localhost:5173';
      window.location.href = `${DOO_EVENTS_URL}/login`;
    } catch (error) {
      console.error('Logout error:', error);
      toast.error("Ocorreu um erro ao fazer logout");
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleConfig = (type: string) => {
    return ROLE_CONFIG[type as keyof typeof ROLE_CONFIG] || ROLE_CONFIG.user;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <Skeleton className="h-10 w-48 mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6">
            <Skeleton className="w-32 h-32 rounded-full mx-auto mb-4" />
            <Skeleton className="h-6 w-32 mx-auto mb-2" />
            <Skeleton className="h-4 w-48 mx-auto" />
          </Card>
          <Card className="lg:col-span-2 p-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="bg-destructive/10 border border-destructive/20 rounded-ds-card p-4">
          <p className="text-destructive">Não foi possível carregar os dados do perfil.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-[16px] font-bold">Meu Perfil</h1>
        <p className="text-muted-foreground mt-1">Gerencie suas informações pessoais e configurações da conta</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="p-6">
          <CardContent className="p-0">
            <div className="text-center">
              {/* Avatar with upload button */}
              <div className="relative inline-block mb-4">
                <Avatar className="w-32 h-32 border-4">
                  <AvatarImage src={profileData.avatar} alt={profileData.name} />
                  <AvatarFallback className="text-3xl font-bold bg-gradient-to-br from-brand-logo-2 to-primary text-on-dark">
                    {getInitials(profileData.name)}
                  </AvatarFallback>
                </Avatar>
                
                <button
                  onClick={handleAvatarClick}
                  disabled={isUploadingAvatar}
                  className="absolute bottom-0 right-0 p-2.5 bg-background rounded-full shadow-lg border hover:bg-accent transition-colors disabled:opacity-50"
                  title="Alterar foto de perfil"
                >
                  <Camera className="w-5 h-5" />
                </button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>

              {/* Name and email */}
              <h2 className="text-[16px] font-semibold mb-1">{profileData.name}</h2>
              <p className="text-muted-foreground mb-3">{profileData.email}</p>

              {/* Role badge */}
              <div className="inline-flex items-center gap-2 mb-4">
                <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getRoleConfig(profileData.type).colorClass}`}>
                  {getRoleConfig(profileData.type).label}
                </span>
              </div>

              {/* Email verification status */}
              <div className="flex items-center justify-center gap-2 text-sm mb-4">
                {profileData.emailVerified ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-status-ok" />
                    <span className="text-status-ok">Email verificado</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-status-attention" />
                    <span className="text-status-attention">Email não verificado</span>
                  </>
                )}
              </div>

              {/* Description */}
              {profileData.description && (
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  {profileData.description}
                </p>
              )}

              {/* Logout button */}
              <Button
                onClick={handleLogout}
                variant="destructive"
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair da conta
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Card className="lg:col-span-2 p-6">
          <CardContent className="p-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[16px] font-semibold">Informações Pessoais</h2>
              {!isEditing ? (
                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-gradient-to-r from-brand-logo-2 to-primary hover:opacity-90"
                >
                  Editar Perfil
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleCancel}
                    disabled={isSaving}
                    variant="outline"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="bg-brand-green hover:bg-brand-green"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {isSaving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Name field */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <UserIcon className="w-4 h-4" />
                  Nome completo
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!isEditing}
                  placeholder="Digite seu nome completo"
                />
                {isEditing && (
                  <p className="text-xs text-muted-foreground mt-1">Mínimo de {MIN_NAME_LENGTH} caracteres</p>
                )}
              </div>

              {/* Email field (read-only) */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <Mail className="w-4 h-4" />
                  Endereço de email
                </label>
                <Input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="cursor-not-allowed opacity-50"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  O email não pode ser alterado diretamente
                </p>
              </div>

              {/* User type field (read-only) */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2">
                  <Shield className="w-4 h-4" />
                  Tipo de usuário
                </label>
                <div className="w-full px-4 py-2.5 bg-accent/50 border rounded-ds-card">
                  <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getRoleConfig(profileData.type).colorClass}`}>
                    {getRoleConfig(profileData.type).label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {getRoleConfig(profileData.type).description}
                </p>
              </div>

              {/* Description field */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Descrição / Bio
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={!isEditing}
                  rows={4}
                  placeholder="Conte um pouco sobre você..."
                />
                {isEditing && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Máximo de {MAX_DESCRIPTION_LENGTH} caracteres ({formData.description.length}/{MAX_DESCRIPTION_LENGTH})
                  </p>
                )}
              </div>

              {/* Account dates */}
              <div className="pt-4 border-t">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Conta criada em</p>
                    <p className="font-medium">
                      {new Date(profileData.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Última atualização</p>
                    <p className="font-medium">
                      {new Date(profileData.updatedAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}